import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { keycloak } from '@/auth';
import { API_BASE_URL } from '@/api/client';

interface PendingUserMessage {
  content: string;
  imageIds?: string[];
  vectorStoreIds?: string[];
}

interface StreamingState {
  isStreaming: boolean;
  content: string;
  reasoningContent: string;
  messageId: string | null;
  userMessageId: string | null;
  pendingUserMessage: PendingUserMessage | null;
  wasStopped: boolean;
}

export function useLlmSocket() {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    reasoningContent: '',
    messageId: null,
    userMessageId: null,
    pendingUserMessage: null,
    wasStopped: false,
  });

  const queryClient = useQueryClient();
  const conversationIdRef = useRef<string | null>(null);

  // Connect to WebSocket
  useEffect(() => {
    if (!keycloak.token) return;

    // WebSocket подключается к корню сервера, не к /api
    // VITE_API_URL может быть /api или https://domain/api - нужно убрать /api
    let wsUrl = API_BASE_URL;
    if (wsUrl.endsWith('/api')) {
      wsUrl = wsUrl.slice(0, -4); // убираем /api
    }
    // Если относительный путь или пустой - используем текущий origin
    if (!wsUrl || wsUrl.startsWith('/')) {
      wsUrl = window.location.origin;
    }

    const sock = io(`${wsUrl}/llm`, {
      auth: { token: keycloak.token },
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    sock.on('connect', () => {
      setIsConnected(true);
    });

    sock.on('disconnect', () => {
      setIsConnected(false);
    });

    // Handle message creation confirmation
    sock.on('message-created', (data: { userMessageId: string; assistantMessageId: string }) => {
      setStreamingState((prev) => ({
        ...prev,
        userMessageId: data.userMessageId,
        messageId: data.assistantMessageId,
      }));
    });

    // Handle streaming delta
    sock.on('delta', (data: { content: string }) => {
      setStreamingState((prev) => ({
        ...prev,
        content: prev.content + data.content,
      }));
    });

    // Handle reasoning summary delta
    sock.on('reasoning-delta', (data: { content: string }) => {
      setStreamingState((prev) => ({
        ...prev,
        reasoningContent: prev.reasoningContent + data.content,
      }));
    });

    // Handle streaming complete
    sock.on('done', () => {
      setStreamingState((prev) => ({
        ...prev,
        isStreaming: false,
        pendingUserMessage: null,
      }));

      // Invalidate conversation query to refresh messages
      if (conversationIdRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['llm-conversation', conversationIdRef.current],
        });
      }
    });

    // Handle stream stopped (user aborted)
    sock.on('stopped', () => {
      setStreamingState((prev) => ({
        ...prev,
        isStreaming: false,
        pendingUserMessage: null,
        wasStopped: true,
      }));

      if (conversationIdRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['llm-conversation', conversationIdRef.current],
        });
      }
    });

    // Handle errors
    sock.on('error', (data: { error: string }) => {
      setStreamingState((prev) => ({
        ...prev,
        isStreaming: false,
        pendingUserMessage: null,
      }));
      toast.error('Ошибка', { description: data.error });

      if (conversationIdRef.current) {
        queryClient.invalidateQueries({
          queryKey: ['llm-conversation', conversationIdRef.current],
        });
      }
    });

    setSocket(sock);

    return () => {
      sock.disconnect();
    };
  }, [keycloak.token, queryClient]);

  // Send message
  const sendMessage = useCallback(
    (
      conversationId: string,
      content: string,
      imageIds?: string[],
      reasoningEffort?: string,
      vectorStoreIds?: string[],
    ) => {
      if (!socket || !isConnected) {
        toast.error('Нет подключения', { description: 'Подключение к серверу отсутствует' });
        return;
      }

      conversationIdRef.current = conversationId;

      setStreamingState({
        isStreaming: true,
        content: '',
        reasoningContent: '',
        messageId: null,
        userMessageId: null,
        pendingUserMessage: { content, imageIds, vectorStoreIds },
        wasStopped: false,
      });

      socket.emit('send-message', {
        conversationId,
        content,
        imageIds,
        reasoningEffort,
        vectorStoreIds,
      });
    },
    [socket, isConnected]
  );

  // Stop streaming
  const stopStream = useCallback(() => {
    if (!socket || !conversationIdRef.current) return;

    socket.emit('stop-stream', { conversationId: conversationIdRef.current });
    setStreamingState((prev) => ({
      ...prev,
      isStreaming: false,
      pendingUserMessage: null,
      wasStopped: true,
    }));

    // Invalidate immediately so partial response is visible
    queryClient.invalidateQueries({
      queryKey: ['llm-conversation', conversationIdRef.current],
    });
  }, [socket, queryClient]);

  // Reset streaming state (for navigation)
  const resetStreaming = useCallback(() => {
    setStreamingState({
      isStreaming: false,
      content: '',
      reasoningContent: '',
      messageId: null,
      userMessageId: null,
      pendingUserMessage: null,
      wasStopped: false,
    });
    conversationIdRef.current = null;
  }, []);

  return {
    isConnected,
    isStreaming: streamingState.isStreaming,
    streamingContent: streamingState.content,
    reasoningContent: streamingState.reasoningContent,
    streamingMessageId: streamingState.messageId,
    pendingUserMessage: streamingState.pendingUserMessage,
    wasStopped: streamingState.wasStopped,
    sendMessage,
    stopStream,
    resetStreaming,
  };
}
